import { ActionTree, MutationTree } from "vuex";
import ProductSearchApi from "@/api/ProductSearchApi";
import { HandlingFee, IProductData, Logger, SurfaceTreatmentFee } from "@iloq/shared";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import { IAdditionalService, IServiceproduct, ResponseStatus, IRootState } from "@iloq/shared";
import { AxiosError } from "axios";

const productSearchApi = new ProductSearchApi();

export interface IProductDataState {
  productData: any;
  serviceproducts: {
    ids: string[];
    products: { [key: string]: IAdditionalService } | null;
  };
  handlingFee?: IProductData | null;
  surfaceTreatmentFee?: IProductData | null;
}

const getServiceproductIds = (serviceproducts: { [key: string]: IAdditionalService }) =>
  Object.keys(serviceproducts)
    .map((key) => {
      return serviceproducts[key].products.map(({ id }) => id);
    })
    .flat();

const initialState: IProductDataState = {
  productData: {},
  serviceproducts: {
    ids: [],
    products: null,
  },
  handlingFee: null,
  surfaceTreatmentFee: null,
};
const actions: ActionTree<IRootState, IRootState> = {
  async getProduct(_: any, id: string): Promise<any> {
    Logger.debug("action, getProduct");
    return productSearchApi.fetchProduct(id).catch(handleCommonApiErrors);
  },
  async getServiceproducts({ commit, rootState, rootGetters }): Promise<any> {
    const { priceListAreaCode } = (rootState as any)?.partnerInfo?.partnerInfo || {};

    const addOrderingAllowed = (product: IServiceproduct) => {
      const orderingAllowedByFamily: boolean = rootGetters["orderingPermission/isOrderingAllowed"](
        product.family,
      );
      const paymentTermsCode: string = (rootState as any)?.partnerInfo?.partnerInfo
        ?.paymentTermsCode;
      const allowedPaymentTermsCode = !(product.blocked_payment_terms || []).includes(
        paymentTermsCode,
      );
      return {
        ...product,
        orderingAllowed: orderingAllowedByFamily && allowedPaymentTermsCode,
      };
    };

    Logger.debug("action, getServiceproducts");
    return productSearchApi
      .getServiceproducts(priceListAreaCode)
      .then((data) => {
        const formattedData = Object.keys(data)
          .map((key) => ({
            [key]: {
              ...data[key],
              products: data[key].products.map(addOrderingAllowed),
            },
          }))
          .reduce((prev, curr) => ({ ...prev, ...curr }), {});

        commit("setServiceproductDataResult", formattedData);
      })
      .catch((err: AxiosError) => {
        if (err?.response?.status === ResponseStatus.forbidden) {
          commit("setServiceproductDataResult", {});
        } else {
          handleCommonApiErrors(err);
        }
      });
  },
  async getHandlingFee({ commit }): Promise<void> {
    Logger.debug("action, getHandlingFee");

    try {
      const data = await productSearchApi.fetchProduct(HandlingFee.sku);
      commit("setHandlingFeeResult", data);
    } catch (error) {
      // if handling fee is not found, ignore the error and leave handling fee as null (default value)
      if ((error as AxiosError)?.response?.status === ResponseStatus.notFound) {
        return;
      }
      handleCommonApiErrors(error as Error);
    }
  },
  async getSurfaceTreatmentFee({ commit }): Promise<void> {
    Logger.debug("action, getSurfaceTreatmentFee");

    try {
      const data = await productSearchApi.fetchProduct(SurfaceTreatmentFee.sku);
      commit("setSurfaceTreatmentFeeResult", data);
    } catch (error) {
      // if surface treatment fee is not found, ignore the error and leave surface treatment fee as null (default value)
      if ((error as AxiosError)?.response?.status === ResponseStatus.notFound) {
        return;
      }
      handleCommonApiErrors(error as Error);
    }
  },
};

const mutations: MutationTree<IProductDataState> = {
  setProductDataResult(state: IProductDataState, result: any): void {
    state.productData = result;
  },
  setServiceproductDataResult(
    state: IProductDataState,
    result: { [key: string]: IAdditionalService },
  ): void {
    state.serviceproducts.products = result;
    state.serviceproducts.ids = getServiceproductIds(result);
  },
  setHandlingFeeResult(state: IProductDataState, result: IProductData): void {
    state.handlingFee = result;
  },
  setSurfaceTreatmentFeeResult(state: IProductDataState, result: IProductData): void {
    state.surfaceTreatmentFee = result;
  },
};

export const productDataModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
